import React, {useEffect, useMemo} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {elementalsSetRandomElements, setSelectedToken, showToast} from '../../../redux/actions';
import {
  selectCurrentPlayer,
  selectElementalsConstructionTokens,
  selectPendingBuildings,
  selectPhase,
  selectRound
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {COMMON_BUILDING_CONFIGS, SPECIAL_BUILDING_CONFIGS} from "../../../configs/tokens";
import {checkIfCentralQuarter} from "../../../utils/buildings/checkIfCentralQuarter";
import {
  ANY_COMMON_BUILDING_TOKEN,
  ANY_SPECIAL_BUILDING_TOKEN,
  CENTRAL_QUARTER_PREFIX,
  COMMON_BUILDING_TOKENS,
  SPECIAL_BUILDING_TOKENS
} from "../../../constants/tokens";
import {useBuildingHints} from "../../../hooks/visuals/useBuildingHints";
import {usePrev} from "../../../hooks/react/usePrev";
import {checkIfAnyBuildingToken} from "../../../utils/buildings/checkIfAnyBuildingToken";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import {selectSelectedToken} from "../../../redux/reducers/privateStateReducer/selectors";
import {FACTIONS} from "../../../constants/factions";
import {isPlayerFaction} from "../../../utils/factions/isPlayerFaction";
import {MINE_EXPLORATION, MINE_EXPLORATION_CONFIG} from "../../../configs/factions/dwarves";
import {useDesktopLayout} from "../../../hooks/visuals/useWideScreenLayout";
import {getBuildingPrice} from "../../../utils/economy";
import {
  addMoreElementalsConstructionTokens
} from "../../../utils/factions/elementals/addMoreElementalsConstructionTokens";
import {TEXT_NOT_ALLOWED_FIRST_ROUND_BUILDINGS} from "../../../constants/text/construction";
import NewBuildingToolbarView from "./NewBuildingToolbarView";
import {checkIfTurnWillBeAutoCompleted} from "../../../utils/gameState/turn/executePhaseAutoActions";
import {checkIfSpecialBuilding} from "../../../utils/buildings/checkIfSpecialBuilding";
import {checkIfConstructionAllowedInRound} from "../../../utils/gameState/construction/checkIfConstructionAllowedInRound";

/**
 * Все строения одним списком, когда описание строения скрыто: места хватает,
 * и переключаться между обычными и специальными строениями незачем. На
 * десктопе блоки в два ряда, уже — по две колонки в блоке (три ряда, у
 * гномов в первом блоке четыре), см. NewBuildingToolbar.css. С описанием
 * колонка справа занята им, и список прежний — обычные или специальные с
 * кнопкой переключения
 * @returns {boolean}
 */
export const useIsAllBuildingsShown = () => {
  const [isHintsVisible] = useBuildingHints();
  return !isHintsVisible;
};

// Порядок блоков списка «все строения» на десктопе. Крепость — спецстроение и
// стоит последней в ряду
const DESKTOP_COMMON_BUILDINGS_ORDER = [
  CENTRAL_QUARTER_PREFIX,
  COMMON_BUILDING_TOKENS.QUARTER,
  COMMON_BUILDING_TOKENS.ROAD,
  COMMON_BUILDING_TOKENS.FARM,
  COMMON_BUILDING_TOKENS.BARRACKS,
  MINE_EXPLORATION,
];
const DESKTOP_SPECIAL_BUILDINGS_ORDER = [
  SPECIAL_BUILDING_TOKENS.PALACE,
  SPECIAL_BUILDING_TOKENS.ARSENAL,
  SPECIAL_BUILDING_TOKENS.TEMPLE,
  SPECIAL_BUILDING_TOKENS.WAREHOUSE,
  SPECIAL_BUILDING_TOKENS.MILITARY_CAMP,
  SPECIAL_BUILDING_TOKENS.GUILD,
  SPECIAL_BUILDING_TOKENS.FORTRESS,
];
// Уже десктопа обычные — в порядке списка, у специальных арсенал перед лагерем
const NARROW_SPECIAL_BUILDINGS_ORDER = [
  SPECIAL_BUILDING_TOKENS.PALACE,
  SPECIAL_BUILDING_TOKENS.WAREHOUSE,
  SPECIAL_BUILDING_TOKENS.ARSENAL,
  SPECIAL_BUILDING_TOKENS.MILITARY_CAMP,
  SPECIAL_BUILDING_TOKENS.TEMPLE,
  SPECIAL_BUILDING_TOKENS.GUILD,
  SPECIAL_BUILDING_TOKENS.FORTRESS,
];

// Строения блока в заданном порядке; не названные в нем остаются в конце в
// прежнем порядке (sort устойчив)
const sortBuildingsByOrder = (buildings, order) => {
  const getRank = (building) => {
    const rank = order.indexOf(checkIfCentralQuarter(building.id) ? CENTRAL_QUARTER_PREFIX : building.id);
    return rank < 0 ? order.length : rank;
  };
  return [...buildings].sort((a, b) => getRank(a) - getRank(b));
};

/**
 * Список «все строения» — двумя блоками: обычные строения, затем специальные.
 * Порядок внутри блоков свой у десктопа и у узкого окна, см.
 * DESKTOP_*_BUILDINGS_ORDER и NARROW_SPECIAL_BUILDINGS_ORDER
 * @param {Array} buildings — список из useBuildings с isAllShown
 * @param {boolean} isAllShown
 * @param {boolean} [isDesktopLayout] — десктопная раскладка (useDesktopLayout)
 * @returns {Array<Array>} блоки списка
 */
export const splitBuildingGroups = (buildings, isAllShown, isDesktopLayout = false) => {
  if (!isAllShown) {
    return [buildings];
  }
  const commonBuildings = buildings.filter((building) => !checkIfSpecialBuilding(building.id));
  const specialBuildings = buildings.filter((building) => checkIfSpecialBuilding(building.id));
  if (specialBuildings.length === 0) {
    return [buildings];
  }
  if (isDesktopLayout) {
    return [
      sortBuildingsByOrder(commonBuildings, DESKTOP_COMMON_BUILDINGS_ORDER),
      sortBuildingsByOrder(specialBuildings, DESKTOP_SPECIAL_BUILDINGS_ORDER),
    ];
  }
  return [commonBuildings, sortBuildingsByOrder(specialBuildings, NARROW_SPECIAL_BUILDINGS_ORDER)];
};

// Блоки списка строений под текущую раскладку окна (см. splitBuildingGroups)
export const useBuildingGroups = (buildings, isAllShown) => {
  const isDesktopLayout = useDesktopLayout();
  return splitBuildingGroups(buildings, isAllShown, isDesktopLayout);
};

// Список строений игрока с ценами: цены — с учетом уже построенного за ход
// (pendingBuildings). isAllShown — обычные и специальные строения сразу, без
// кнопок переключения (см. useIsAllBuildingsShown)
export function useBuildings(isSpecial, currentPlayer, currentRound, pendingBuildings, isAllShown = false) {
  return useMemo(
    () => getBuildingsList(isSpecial, currentPlayer, pendingBuildings, isAllShown),
    [isSpecial, isAllShown, currentPlayer, pendingBuildings]
  );
}

export const getBuildingsList = (isSpecial, currentPlayer, pendingBuildings, isAllShown = false) => {
  if (!currentPlayer) {
    return [];
  }

  let resultBuildings;
  if (isSpecial && !isAllShown) {
    resultBuildings = [...SPECIAL_BUILDING_CONFIGS, ANY_COMMON_BUILDING_TOKEN];
  } else {
    let commonBuildings = COMMON_BUILDING_CONFIGS.filter(building => {
      const isCentralQuarter = checkIfCentralQuarter(building.id);

      if (!isCentralQuarter) {
        return true
      }

      const centralQuarterColor = building.id.replace(CENTRAL_QUARTER_PREFIX, '');

      return centralQuarterColor === currentPlayer.color;
    });

    if (isPlayerFaction(currentPlayer, FACTIONS.DWARVES)) {
      commonBuildings.push(MINE_EXPLORATION_CONFIG);
    }

    if (isAllShown) {
      resultBuildings = [...commonBuildings, ...SPECIAL_BUILDING_CONFIGS];
    } else {
      commonBuildings.push(ANY_SPECIAL_BUILDING_TOKEN);
      resultBuildings = commonBuildings;
    }
  }

  return resultBuildings.map(building => {
    const {priceCoins, isDiscount, isFreeAction} = getBuildingPrice(building.id, currentPlayer, pendingBuildings);
    return {
      ...building,
      priceCoins,
      isDiscount,
      isFreeAction,
    }
  });
};

// Строение, выбираемое по умолчанию: в первом раунде — базовый квартал, в
// остальных — первое доступное строение (центральный квартал игрока)
export const getDefaultBuildingToken = (buildings, currentRound) => {
  const quarterToken = buildings.find(b => b.id.includes('quarter') && !checkIfCentralQuarter(b.id));
  if (currentRound === 1 && quarterToken) {
    return quarterToken.id;
  }
  return buildings.length > 0 ? buildings[0].id : null;
};

const NewBuildingToolbar = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const selectedToken = useSelector(selectSelectedToken);
  const phase = useSelector(selectPhase);
  const currentPlayer = useSelector(selectCurrentPlayer);
  const currentRound = useSelector(selectRound);
  const pendingBuildings = useSelector(selectPendingBuildings);
  const elementalsConstructionTokens = useSelector(selectElementalsConstructionTokens);

  // Список — по уже выбранному строению: панель монтируется в начале своего
  // хода с выбором, перенесенным из плана (constructionPlanMiddleware), и
  // пересоздается после инспекции ячейки — спецстроение должно остаться в
  // своем списке, иначе его не будет в buildings и выбор уедет в дефолт
  const [isSpecial, setIsSpecial] = React.useState(() => checkIfSpecialBuilding(selectedToken));
  const isAllShown = useIsAllBuildingsShown();

  const handleSelect = (tokenId, isDisabledFirstRound) => {
    if (isDisabledFirstRound) {
      dispatch(showToast(TEXT_NOT_ALLOWED_FIRST_ROUND_BUILDINGS, 'warning', 5000));
      return;
    }
    if (checkIfAnyBuildingToken(tokenId)) {
      dispatch(setSelectedToken(null));
    } else {
      dispatch(setSelectedToken(tokenId));
    }

    REGISTRY_SINGLETON.getSoundManager().playTokenClick(tokenId);

    if (checkIfAnyBuildingToken(tokenId)) {
      setIsSpecial(!isSpecial);
    } else if (isAllShown) {
      // Список не переключается, но помнит, где выбранное строение: включат
      // описание — откроется список с ним, и выбор не сбросится
      setIsSpecial(checkIfSpecialBuilding(tokenId));
    }
  };

  const buildings = useBuildings(isSpecial, currentPlayer, currentRound, pendingBuildings, isAllShown);
  const buildingGroups = useBuildingGroups(buildings, isAllShown);

  const prevCurrentPlayer = usePrev(currentPlayer);
  const prevIsSpecial = usePrev(isSpecial);

  // Автоматически выбираем токен quarters при первом раунде,
  // В других раундах - первое доступное строение, коим является центральный квартал игрока
  //
  // Уже сделанный выбор при этом сохраняется: панель монтируется в начале
  // своего хода (строение могло быть выбрано в плане, см.
  // constructionPlanMiddleware) и заново — после инспекции ячейки. Дефолт
  // ставится, только когда выбирать реально нечего: список переключили,
  // сменился ходящий игрок (hot seat) или выбранного строения в списке нет
  // (жетон чужой фракции, отряд, флаг, запрещенное в первом раунде)
  useEffect(() => {
    // В списке «все строения» isSpecial только следит за выбором (см.
    // handleSelect) — это не переключение списка
    const isListToggled = !isAllShown && prevIsSpecial !== undefined && prevIsSpecial !== isSpecial;
    const isPlayerChanged = Boolean(prevCurrentPlayer) && currentPlayer.id !== prevCurrentPlayer.id;
    const isSelectedTokenAvailable = Boolean(selectedToken)
      && buildings.some((building) => building.id === selectedToken)
      && checkIfConstructionAllowedInRound(selectedToken, currentRound);
    if (!isListToggled && !isPlayerChanged && isSelectedTokenAvailable) {
      return;
    }
    const defaultToken = getDefaultBuildingToken(buildings, currentRound);
    if (defaultToken && defaultToken !== selectedToken) {
      dispatch(setSelectedToken(defaultToken));
    }
  }, [
    phase, currentPlayer, prevCurrentPlayer, buildings, dispatch, currentRound, isSpecial, prevIsSpecial, selectedToken,
    isAllShown,
  ]);

  const prevPlayerId = usePrev(currentPlayer.id);

  // Список специальных строений сбрасывается на смене ходящего игрока, но не
  // при монтировании: там он выставлен по уже выбранному строению
  useEffect(() => {
    if (prevPlayerId !== undefined && prevPlayerId !== currentPlayer.id && isSpecial) {
      setIsSpecial(false);
    }
  }, [prevPlayerId, currentPlayer.id, isSpecial, setIsSpecial]);

  useEffect(() => {
    // Игрок и жетоны — одним снимком стора в момент запуска, а не из рендера:
    // эффект может запуститься повторно раньше перерисовки (StrictMode в dev
    // гоняет эффекты при монтировании дважды) и по жетонам рендера вытянул бы
    // второй набор, а к запуску ход мог уже перейти к другому игроку
    const state = store.getState();
    const player = selectCurrentPlayer(state);
    if (!player || player.id !== currentPlayer.id || !isPlayerFaction(player, FACTIONS.ELEMENTALS)) {
      return;
    }
    // Ход, который сейчас сам пропустит исполнитель автодействий (у игрока нет
    // городов), — тянуть не для чего: в сетевой игре действие дошло бы до
    // сервера уже после пропуска, и он отверг бы его как устаревшее, показав
    // игроку оверлей переподключения
    if (checkIfTurnWillBeAutoCompleted(store)) {
      return;
    }

    const currentTokens = selectElementalsConstructionTokens(state) || [];
    const additionalTokensCount = (player.constructionPhaseActions || 0) - currentTokens.length;
    if (additionalTokensCount <= 0) {
      return;
    }
    const newElementalsTokens = addMoreElementalsConstructionTokens(currentTokens, additionalTokensCount);
    // Мешок жетонов конечен (ELEMENTALS_CONSTRUCTION_BAG — 9 штук), а действий
    // строительства может быть больше (дворец, политики, дев-флаг
    // REACT_APP_ADDITIONAL_ACTIONS дает 10 базовых). Когда мешок исчерпан,
    // помощник возвращает НОВЫЙ массив прежней длины: диспатч менял
    // идентичность elementalsConstructionTokens, эффект перезапускался по
    // собственной зависимости, и цикл не сходился («Maximum update depth
    // exceeded»). Диспатчим, только если жетоны реально добавились
    if (newElementalsTokens.length > currentTokens.length) {
      dispatch(elementalsSetRandomElements(newElementalsTokens));
    }
  }, [
    currentPlayer.id, currentPlayer.factionId, currentPlayer.constructionPhaseActions, elementalsConstructionTokens,
    dispatch, store,
  ]);

  return (
    <NewBuildingToolbarView
      buildings={buildings}
      buildingGroups={buildingGroups}
      selectedToken={selectedToken}
      currentRound={currentRound}
      availableCoins={currentPlayer?.coins}
      onSelect={handleSelect}
    />
  );
};

export default NewBuildingToolbar;
